import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { RutInput } from "@/components/RutInput";
import { validateRut } from "@/lib/rut";
import { handleApiError } from "@/lib/api-error";
import { useUser } from "@/contexts/UserContext";
import { EmpresaIdentidadCard } from "@/components/EmpresaIdentidadCard";
import { EditNote } from "@/components/ds/icons";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { DriveStep } from "driver.js";
import { PageHeader } from "@/components/ui/PageHeader";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";

interface EmpresaPayload {
  rut: string;
  razon_social: string;
  nombre_fantasia: string;
  email: string;
  email_para_registros: string;
  email_para_vencimientos: string;
  telefono: string;
  giro: string;
  direccion: string;
}

type EmpresaResponse = EmpresaPayload & {
  id: number;
  logo_url: string | null;
  tiene_mandantes: boolean;
};

const emptyEmpresa: EmpresaPayload = {
  rut: "",
  razon_social: "",
  nombre_fantasia: "",
  email: "",
  email_para_registros: "",
  email_para_vencimientos: "",
  telefono: "",
  giro: "",
  direccion: "",
};

const CAMPOS = Object.keys(emptyEmpresa) as (keyof EmpresaPayload)[];

/** Normaliza la respuesta del API al shape del formulario (null → ""). */
function aFormulario(data: Partial<EmpresaPayload>): EmpresaPayload {
  return CAMPOS.reduce((acc, campo) => {
    acc[campo] = data[campo] ?? "";
    return acc;
  }, {} as EmpresaPayload);
}

function hayCambios(a: EmpresaPayload, b: EmpresaPayload): boolean {
  return CAMPOS.some((campo) => a[campo] !== b[campo]);
}

function buildEmpresaGuideSteps(): DriveStep[] {
  return [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "Datos de tu empresa",
        description:
          "Completa los datos generales de tu empresa. Esta información aparece en todos los documentos que genera el sistema.",
      },
    },
    // Paso 2 — Highlight de la sección de datos generales
    {
      element: '[data-tour="empresa-datos"]',
      popover: {
        title: "Información general",
        description:
          "Completa el RUT, razón social, nombre de fantasía y datos de contacto. Esta información aparece en todos los documentos que genera el sistema.",
      },
    },
    // Paso 3 — Highlight del ítem de menú "Documentos Empresa"
    {
      element: '[data-tour="menu-capacitaciones"]',
      popover: {
        title: "Documentos institucionales y capacitaciones",
        description:
          "Sube el reglamento interno, la matriz de riesgo y los demás documentos institucionales desde Documentos Empresa. Los que marques para capacitaciones alimentan la generación de capacitaciones de prevención según DS 76 y Ley 16.744.",
      },
    },
    // Paso 4 — Popover de cierre (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en el botón de ayuda en la parte superior de la página.",
      },
    },
  ];
}

/**
 * Rótulo de sección: 11px en versales sobre la tarjeta. Es deliberadamente más
 * callado que `fc-section-title` — acá la tarjeta agrupa campos y no compite
 * por atención con el título de la página.
 */
function RotuloSeccion({ children }: { children: ReactNode }) {
  return <span className="fc-th text-soft tracking-[0.08em]">{children}</span>;
}

/** Campo de una línea dentro de la grilla de "Datos de la empresa". */
function Campo({
  id,
  etiqueta,
  requerido = false,
  ayuda,
  children,
}: {
  id: string;
  etiqueta: string;
  requerido?: boolean;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[5px]">
      <Label htmlFor={id} className="text-strong gap-1">
        {etiqueta}
        {requerido && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {ayuda && <span className="text-soft text-[11px] leading-[1.45]">{ayuda}</span>}
    </div>
  );
}

/**
 * Fila de ajuste: qué hace a la izquierda, el control a la derecha, separada de
 * la anterior por un divisor. La descripción va al lado del control y no bajo un
 * rótulo suelto porque cada casilla de correo hace algo distinto: sin esa frase
 * las tres se ven iguales.
 */
function FilaAjuste({
  titulo,
  descripcion,
  control,
  htmlFor,
}: {
  titulo: string;
  descripcion?: string;
  control: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="border-border-faint flex flex-wrap items-center gap-3.5 border-t px-[18px] py-3">
      <div className="flex min-w-[190px] flex-1 flex-col gap-0.5">
        <Label htmlFor={htmlFor} className="text-[12.5px] font-medium">
          {titulo}
        </Label>
        {descripcion && (
          <span className="text-soft text-[11.5px] leading-[1.45]">{descripcion}</span>
        )}
      </div>
      {control}
    </div>
  );
}

/** Alto y ancho del control de una fila de ajuste. */
const CONTROL_FILA = "h-[34px] min-w-[220px] flex-1";

/**
 * Frecuencias con que la app revisa si hay algo nuevo. Son las mismas cinco que
 * ofrece fc-app en su pantalla de sincronización; acá van en solo lectura.
 */
const FRECUENCIA_APP = [
  { valor: "1", label: "Cada 1 hora" },
  { valor: "3", label: "Cada 3 horas" },
  { valor: "6", label: "Cada 6 horas" },
  { valor: "12", label: "Cada 12 horas" },
  { valor: "24", label: "Cada 24 horas" },
];

export function Empresa() {
  const { refreshUser } = useUser();
  const [formData, setFormData] = useState<EmpresaPayload>(emptyEmpresa);
  // Último estado confirmado por el API: es el punto de comparación del aviso
  // "tienes cambios sin guardar" y lo que restaura "Descartar".
  const [guardado, setGuardado] = useState<EmpresaPayload>(emptyEmpresa);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tieneMandantes, setTieneMandantes] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showRutError, setShowRutError] = useState(false);

  const dirty = hayCambios(formData, guardado);

  // --- Guía de Empresa ---
  const { startGuide } = useGuide("guide-empresa", { buildSteps: buildEmpresaGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-empresa", startGuide, { anchorSelector: '[data-tour="empresa-datos"]' });
  useGuideAutostart("guide-empresa", startGuide, { anchorSelector: '[data-tour="empresa-datos"]' });
  // --- fin Guía de Empresa ---

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: EmpresaResponse }>("/me/empresa")
      .then((res) => {
        if (cancelled) return;
        const datos = aFormulario(res.data);
        setFormData(datos);
        setGuardado(datos);
        setLogoUrl(res.data.logo_url ?? null);
        setTieneMandantes(res.data.tiene_mandantes ?? false);
      })
      .catch((err) => {
        if (cancelled) return;
        handleApiError(err, "No se pudieron cargar los datos de la empresa");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const descartar = () => {
    setFormData(guardado);
    setShowRutError(false);
  };

  const handleSave = async () => {
    if (!formData.rut.trim() || !formData.razon_social.trim() || !formData.nombre_fantasia.trim()) {
      toast.error("RUT, razón social y nombre de fantasía son obligatorios");
      return;
    }

    if (!validateRut(formData.rut)) {
      setShowRutError(true);
      toast.error("El RUT es inválido");
      return;
    }

    setIsSaving(true);
    try {
      const res = await api.put<{ data: EmpresaPayload }>("/me/empresa", formData);
      const datos = aFormulario(res.data);
      setFormData(datos);
      setGuardado(datos);
      setShowRutError(false);
      await refreshUser();
      toast.success("Datos de la empresa actualizados");
    } catch (err) {
      handleApiError(err, "No se pudieron guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="animate-fc-in flex max-w-[900px] flex-col gap-4">
      <PageHeader
        title="Mi Empresa"
        subtitle="Los datos impresos en cada evidencia, y cómo se comporta tu app móvil."
      />

      <EmpresaIdentidadCard
        logoUrl={logoUrl}
        onChange={setLogoUrl}
        razonSocial={guardado.razon_social}
        rut={guardado.rut}
        esContratista={tieneMandantes}
        isLoading={isLoading}
      />

      {/* --- Datos de la empresa ------------------------------------------ */}
      <section
        className="bg-card flex flex-col gap-3.5 rounded-2xl border px-[18px] py-4"
        data-tour="empresa-datos"
      >
        <RotuloSeccion>Datos de la empresa</RotuloSeccion>

        {isLoading ? (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-x-4 gap-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-[5px]">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-x-4 gap-y-3">
              <Campo id="rut" etiqueta="RUT" requerido>
                <RutInput
                  id="rut"
                  value={formData.rut}
                  onChange={(clean) => setFormData((prev) => ({ ...prev, rut: clean }))}
                  disabled={isSaving}
                  placeholder="12.345.678-9"
                  className="fc-num"
                  validate
                  showError={showRutError}
                />
              </Campo>
              <Campo id="telefono" etiqueta="Teléfono">
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="+56 2 2123 4567"
                />
              </Campo>
              <Campo id="razon_social" etiqueta="Razón social" requerido>
                <Input
                  id="razon_social"
                  value={formData.razon_social}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="Mi Empresa Ltda."
                />
              </Campo>
              <Campo
                id="nombre_fantasia"
                etiqueta="Nombre de fantasía"
                requerido
                ayuda="Es el nombre que ven los trabajadores en la app."
              >
                <Input
                  id="nombre_fantasia"
                  value={formData.nombre_fantasia}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="Mi Empresa"
                />
              </Campo>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-x-4 gap-y-3">
              <Campo id="giro" etiqueta="Giro">
                <Input
                  id="giro"
                  value={formData.giro}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="Construcción y obras civiles"
                />
              </Campo>
              <Campo id="direccion" etiqueta="Dirección">
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="Av. Providencia 1234, Santiago"
                />
              </Campo>
            </div>
          </>
        )}
      </section>

      {/* --- Correos de la empresa ---------------------------------------- */}
      <section className="bg-card overflow-hidden rounded-2xl border">
        <div className="flex flex-col gap-0.5 px-[18px] pt-3.5 pb-3">
          <RotuloSeccion>Correos de la empresa</RotuloSeccion>
          <span className="text-meta text-xs leading-[1.45]">
            Cada casilla recibe un tipo distinto de aviso. Pueden ser la misma.
          </span>
        </div>

        {isLoading ? (
          <div className="flex flex-col">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="border-border-faint flex flex-wrap items-center gap-3.5 border-t px-[18px] py-3"
              >
                <div className="flex min-w-[190px] flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-56" />
                </div>
                <Skeleton className={cn(CONTROL_FILA, "rounded-lg")} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <FilaAjuste
              htmlFor="email"
              titulo="Contacto"
              descripcion="Aparece en los documentos como correo oficial de la empresa."
              control={
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="contacto@empresa.cl"
                  className={CONTROL_FILA}
                />
              }
            />
            <FilaAjuste
              htmlFor="email_para_registros"
              titulo="Registros firmados"
              descripcion="Recibe copia oculta de cada documento firmado en terreno."
              control={
                <Input
                  id="email_para_registros"
                  type="email"
                  value={formData.email_para_registros}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="registros@empresa.cl"
                  className={CONTROL_FILA}
                />
              }
            />
            <FilaAjuste
              htmlFor="email_para_vencimientos"
              titulo="Vencimientos"
              descripcion="Avisa antes de que venza un documento. El trabajador recibe copia de los suyos."
              control={
                <Input
                  id="email_para_vencimientos"
                  type="email"
                  value={formData.email_para_vencimientos}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="vencimientos@empresa.cl"
                  className={CONTROL_FILA}
                />
              }
            />
          </>
        )}
      </section>

      {/* --- App móvil -----------------------------------------------------
          En SOLO LECTURA a propósito: estos ajustes viven hoy en cada teléfono
          (`SyncConfigService` de fc-app) y el API no expone campos para fijarlos
          por empresa. Se muestran con los valores con que sale la app, para que
          se vea qué hace en terreno sin prometer un control que no existe.
          Cuando el backend los soporte, esta sección se vuelve editable. */}
      <section className="bg-card overflow-hidden rounded-2xl border">
        <div className="flex flex-col gap-0.5 px-[18px] pt-3.5 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <RotuloSeccion>App móvil</RotuloSeccion>
            <Badge variant="neutral">Próximamente</Badge>
          </div>
          <span className="text-meta text-xs leading-[1.45]">
            Así viene configurada la app. Hoy cada trabajador puede cambiarlo desde su teléfono;
            pronto vas a poder fijarlo para toda la empresa desde acá.
          </span>
        </div>

        <FilaAjuste
          titulo="Sincronización automática"
          descripcion="La app sube los documentos firmados en cuanto recupera señal."
          control={<Switch checked disabled aria-label="Sincronización automática" />}
        />
        <FilaAjuste
          titulo="Descargar solo con WiFi"
          descripcion="Evita gastar datos móviles en faena. Las firmas siempre suben, con o sin WiFi."
          control={<Switch checked={false} disabled aria-label="Descargar solo con WiFi" />}
        />
        <FilaAjuste
          htmlFor="app_frecuencia"
          titulo="Cada cuánto revisa si hay algo nuevo"
          descripcion="Solo aplica con la sincronización automática encendida."
          control={
            <NativeSelect
              id="app_frecuencia"
              size="sm"
              defaultValue="3"
              disabled
              className="min-w-[190px]"
            >
              {FRECUENCIA_APP.map((opcion) => (
                <NativeSelectOption key={opcion.valor} value={opcion.valor}>
                  {opcion.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          }
        />
        <FilaAjuste
          htmlFor="app_retencion"
          titulo="Borrar documentos antiguos del teléfono"
          descripcion="Los documentos quedan en el teléfono hasta que el trabajador los borre. La copia de la plataforma no se toca."
          control={
            <NativeSelect
              id="app_retencion"
              size="sm"
              defaultValue="nunca"
              disabled
              className="min-w-[190px]"
            >
              <NativeSelectOption value="nunca">Nunca (mantener todo)</NativeSelectOption>
            </NativeSelect>
          }
        />
      </section>

      {/* --- Barra de cambios sin guardar -----------------------------------
          Reemplaza al botón "Guardar" fijo al pie del formulario: aparece sólo
          cuando hay algo que guardar y acompaña al scroll, así no hay que ir a
          buscarlo al final de la página. */}
      {dirty && (
        <div
          role="region"
          aria-label="Cambios sin guardar"
          className="animate-fc-in bg-secondary sticky bottom-3.5 z-20 flex flex-wrap items-center gap-3 rounded-xl px-4 py-[11px] text-white shadow-[var(--shadow-toast)]"
        >
          {/* `basis-full` hasta `sm`: en pantallas angostas el aviso se lleva su
              propia fila y los dos botones bajan juntos, en vez de partirse el
              mensaje en dos renglones o dejar el primario solo abajo a la
              izquierda. `grow` y `basis` en vez de `flex-1` porque `flex-1` es
              una abreviatura que también fija la base y pisaría a `basis-full`. */}
          <div className="flex min-w-0 grow basis-full items-center gap-3 sm:basis-auto">
            <EditNote className="text-primary-on-dark size-[18px] shrink-0" />
            <span className="text-[12.5px]">Tienes cambios sin guardar.</span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="xs"
              onClick={descartar}
              disabled={isSaving}
              className="text-white/70 hover:bg-white/10 hover:text-white"
            >
              Descartar
            </Button>
            <Button size="xs" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
